import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowRight } from 'lucide-react';
import { MagneticButton } from './effects/MagneticButton';
import { useReducedMotion } from './hooks/useReducedMotion';

gsap.registerPlugin(ScrollTrigger);

export function FinalCTASection() {
  const sectionRef = useRef<HTMLElement>(null);
  const prefersReduced = useReducedMotion();
  const navigate = useNavigate();

  useEffect(() => {
    if (prefersReduced || !sectionRef.current) return;

    const ctx = gsap.context(() => {
      gsap.from('.cta-headline', {
        opacity: 0,
        y: 40,
        duration: 0.8,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 75%',
          toggleActions: 'play none none none',
        },
      });

      gsap.from('.cta-subtitle', {
        opacity: 0,
        y: 30,
        duration: 0.7,
        delay: 0.2,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 75%',
          toggleActions: 'play none none none',
        },
      });

      gsap.from('.cta-button', {
        opacity: 0,
        scale: 0.9,
        duration: 0.6,
        delay: 0.4,
        ease: 'back.out(1.5)',
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 75%',
          toggleActions: 'play none none none',
        },
      });

      gsap.from('.cta-below', {
        opacity: 0,
        duration: 0.5,
        delay: 0.6,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 75%',
          toggleActions: 'play none none none',
        },
      });
    }, sectionRef);

    return () => ctx.revert();
  }, [prefersReduced]);

  return (
    <section
      ref={sectionRef}
      className="relative flex min-h-screen items-center justify-center overflow-hidden bg-landing-bg"
    >
      {/* Purple radial glow */}
      <div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
        style={{
          width: '50vw',
          height: '50vw',
          maxWidth: '700px',
          maxHeight: '700px',
          background: 'radial-gradient(ellipse, rgba(124,58,237,0.12) 0%, transparent 70%)',
          filter: 'blur(60px)',
        }}
      />

      {/* Gradient overlay from top */}
      <div
        className="absolute inset-x-0 top-0 h-48 pointer-events-none"
        style={{
          background: 'linear-gradient(to bottom, #0D0D1A, transparent)',
        }}
      />

      <div className="relative z-10 mx-auto max-w-[700px] px-6 text-center">
        {/* Headline */}
        <h2
          className="cta-headline"
          style={{
            fontFamily: "'Space Grotesk', system-ui, sans-serif",
            fontSize: 'clamp(2.5rem, 5vw + 1rem, 5rem)',
            fontWeight: 700,
            lineHeight: 1.05,
            background: 'linear-gradient(135deg, #E8E6F0 30%, #7C3AED 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          That's it. Five minutes
          <br />
          to content superpowers.
        </h2>

        {/* Subtitle */}
        <p
          className="cta-subtitle mx-auto mt-6 max-w-[45ch] text-[#C8C6D2]"
          style={{
            fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
            fontSize: 'clamp(1.25rem, 1vw + 0.9rem, 1.5rem)',
            lineHeight: 1.65,
          }}
        >
          No technical skills required. If you can type, you can create professional,
          psychology-powered content with Zupa.
        </p>

        {/* CTA button */}
        <div className="cta-button mt-10">
          <MagneticButton strength={0.25}>
            <button
              onClick={() => navigate('/register')}
              className="group inline-flex items-center gap-2 rounded-full bg-zupa-purple px-10 py-4 text-base font-semibold text-white transition-all duration-300 hover:bg-zupa-purple-light hover:shadow-[0_0_45px_rgba(124,58,237,0.5)] active:scale-[0.98]"
              style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}
            >
              Get Started Free
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </MagneticButton>
        </div>

        {/* Below CTA text */}
        <p
          className="cta-below mt-8 text-[#C8C6D2]/60"
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: '1rem',
            letterSpacing: '0.03em',
          }}
        >
          15-day free trial. No credit card required.
        </p>
      </div>
    </section>
  );
}
